import styled from "@emotion/styled";
import TariffInfo from "../elements/TariffInfo";
import NavigationButton from "../elements/NavigationButton";
import { useState } from "react";
import CurrentMeasurement from "./currentMeasurement/CurrentMeasurement";
import SpeedtestGuide from "./speedtestGuide/SpeedtestGuide";
import AdditionalInfo from "./additionalInfo/AdditionalInfo";
import History from "./history/History";
import { getAllRecords } from "../../../features/history/getAllRecords";
import { nullOrValue } from "../../../helper/utils";
import { useAppSelector } from "../../../hooks";
import t from "../../../helper/t";
import CommonContainer from "../elements/CommonContainer";
import WhiteContainer from "./WhiteContainer";

const RootDiv = styled.div(({ theme }) => ([
  {
    flexGrow: 10,
  }
]));

const TariffInfoContainer = styled.div([
  {
    width: "100%",
  }
]);

const ContainerDiv = styled.div(({ theme }) => ([
  {
    display: "flex",
    flexDirection: "column",
    paddingTop: 14,
    paddingLeft: 20,
    paddingRight: 20,

    /* for mobile */
    [theme.splus3.breakpoints.mobile]: [
      {
        paddingLeft: 16,
        paddingRight: 16,
      }
    ],
  }
]));

const HeaderTextDiv = styled.div(({ theme }) => ([
  theme.splus3.foreground.monochrome600,
  theme.splus3.typography.h2,
  {
    marginBottom: 48,

    /* for mobile */
    [theme.splus3.breakpoints.mobile]: [
      theme.splus3.typography.font24,
      {
        lineHeight: "28px",
        marginBottom: 20,
      }
    ],
  }
]));

const TabContainer = styled.div(({ theme }) => ([
  {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gridTemplateRows: "1fr",
    alignSelf: "center",
    columnGap: 8,
    maxWidth: 800,
    width: "100%",
    overflowX: "scroll",


    /* for mobile */
    [theme.splus3.breakpoints.mobile]: [
      {
        columnGap: 12,
        paddingLeft: 28,
        paddingRight: 28,
        width: "calc(100% - 56px)",
      }
    ],
  }
]));

const TabContent = styled.div(({ theme }) => ([
  {
    display: "flex",
    flexDirection: "column",
    rowGap: 8,

    /* for mobile */
    [theme.splus3.breakpoints.mobile]: [
      {
        rowGap: 4,
      }
    ],
  },
]));

interface ButtonContentProps {
  isSelected: boolean;
}

const ButtonContent = styled.div<ButtonContentProps>(({ theme, isSelected }) => ([
  theme.splus3.foreground.monochrome600,
  isSelected ? theme.splus3.typography.defaultBold : theme.splus3.typography.default,
  {
    textAlign: "center",
    lineHeight: "26px",
    whiteSpace: "nowrap",

    /* for mobile */
    [theme.splus3.breakpoints.mobile]: [
      isSelected ? theme.splus3.typography.smallBold : theme.splus3.typography.small,
      isSelected ?
        {
          marginLeft: 12,
          marginRight: 12,
        } : {}
    ],
  },
]));

const UnderlineDiv = styled.div<ButtonContentProps>(({ theme, isSelected }) => ([
  theme.splus3.background.vodafoneRed,
  {
    height: 4,
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    opacity: isSelected ? 1 : 0,
  },
]));

enum TabKey {
  CurrentMesasurement,
  SpeedtestGuide,
  AdditionalInfo,
  History,
}

interface ResultPageProps {
}

const ResultPage: React.FC<ResultPageProps> = () => {
  const historyState = useAppSelector(state => state.history);
  const sortedRecords = getAllRecords(historyState);
  const lastRecord = sortedRecords[0];
  const bookedDownloadSpeed = nullOrValue(lastRecord?.prepareResult.init.modem.bookedDownloadSpeedMax);
  const bookedUploadSpeed = nullOrValue(lastRecord?.prepareResult.init.modem.bookedUploadSpeedMax);

  const [currentTab, setCurrentTab] = useState(TabKey.CurrentMesasurement);

  const getTitle = (): string => {
    switch (currentTab) {
      case TabKey.CurrentMesasurement: return t("Dein Ergebnis");
      case TabKey.SpeedtestGuide: return t("Dein Speedtest Guide");
      case TabKey.AdditionalInfo: return t("Zusätzliche Infos");
      case TabKey.History: return t("Frühere Ergebnisse");
    }
  }

  const navButton = (name: string, tab: TabKey) => {
    const isSelected = tab === currentTab;

    return (
      <NavigationButton onClick={() => setCurrentTab(tab)} isEnabled={!isSelected}>
        <TabContent>
          <ButtonContent isSelected={isSelected}>
            {name}
          </ButtonContent>
          <UnderlineDiv isSelected={isSelected} />
        </TabContent>
      </NavigationButton>
    );
  }

  const content = () => {
    switch (currentTab) {
      case TabKey.CurrentMesasurement: return <CurrentMeasurement showInfo={true} navigateToGuide={() => setCurrentTab(TabKey.SpeedtestGuide)} />;
      case TabKey.SpeedtestGuide: return <SpeedtestGuide />;
      case TabKey.AdditionalInfo: return <AdditionalInfo />;
      case TabKey.History: return <History />;
    }
  }

  return (
    <RootDiv>
      <WhiteContainer>
        <TariffInfoContainer>
          <TariffInfo fading={false} bookedDownloadSpeed={bookedDownloadSpeed} bookedUploadSpeed={bookedUploadSpeed} />
        </TariffInfoContainer>

        <CommonContainer>
          <ContainerDiv>
            <HeaderTextDiv>
              {getTitle()}
            </HeaderTextDiv>
          </ContainerDiv>

          <TabContainer>
            {navButton(t("Aktuelle Messung"), TabKey.CurrentMesasurement)}
            {navButton(t("Speedtest Guide"), TabKey.SpeedtestGuide)}
            {navButton(t("Zusätzliche Infos"), TabKey.AdditionalInfo)}
            {navButton(t("Frühere Ergebnisse"), TabKey.History)}
          </TabContainer>
        </CommonContainer>
      </WhiteContainer>

      {content()}
    </RootDiv>
  );
}

export default ResultPage;
